import type { UseFormReturn } from 'react-hook-form';
import { Disclosure, DisclosureButton, Labeled, Select } from '@sixui/ui';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IFormatsSettingsFormValues } from './FormatsSettingsForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';

export type IFormatsSettingsFormDatesSectionProps = IContainerProps & {
  form: UseFormReturn<IFormatsSettingsFormValues>;
};

const dateFormats = [
  {
    // 31 décembre 1999 01:59
    format: 'DDD t',
    value: 'LLL',
  },
  {
    // 31 déc. 1999 13:59
    format: 'DD t',
    value: 'lll',
  },
  {
    // 31 décembre 1999
    format: 'DDD',
    value: 'LL',
  },
  {
    // 31 déc. 1999
    format: 'DD',
    value: 'll',
  },
  {
    // 31/12/1999 01:59
    format: 'D t',
    value: 'L HH:mm',
  },
  {
    // 31/12/1999
    format: 'D',
    value: 'L',
  },
  {
    // 01:59
    format: 't',
    value: 'HH:mm',
  },
];

const getFormattedDateSample = (format: string, locale: string): string => {
  const dateIso = '1999-12-31T13:59:00';
  const date = DateTime.fromISO(dateIso).setLocale(locale);
  const formattedDate = date.toFormat(format);

  return formattedDate;
};

export const FormatsSettingsFormDatesSection: React.FC<
  IFormatsSettingsFormDatesSectionProps
> = ({ sx, form }) => {
  const { i18n, t } = useTranslation();
  const locale = i18n.language;

  return (
    <Disclosure
      sx={sx}
      defaultExpanded
      trigger={<DisclosureButton>Dates</DisclosureButton>}
    >
      <FormSection sub>
        <Labeled
          label={t(
            'FormatsSettingsFormDatesSection.fields.articleDateFormat.label',
          )}
          required
        >
          <FormField
            name="articleDateFormat"
            control={form.control}
            as={Select}
            forwardError
            items={dateFormats.map(({ format, value }) => ({
              value,
              label: getFormattedDateSample(format, locale),
            }))}
          />
        </Labeled>
        <Labeled
          label={t(
            'FormatsSettingsFormDatesSection.fields.categoryDateFormat.label',
          )}
          required
        >
          <FormField
            name="categoryDateFormat"
            control={form.control}
            as={Select}
            forwardError
            items={dateFormats.map(({ format, value }) => ({
              value,
              label: getFormattedDateSample(format, locale),
            }))}
          />
        </Labeled>
        <Labeled
          label={t(
            'FormatsSettingsFormDatesSection.fields.feedDateFormat.label',
          )}
          required
        >
          <FormField
            name="feedDateFormat"
            control={form.control}
            as={Select}
            forwardError
            items={dateFormats.map(({ format, value }) => ({
              value,
              label: getFormattedDateSample(format, locale),
            }))}
          />
        </Labeled>
      </FormSection>
    </Disclosure>
  );
};
